import React, { useState, useEffect, useRef, useCallback, useMemo, memo } from 'react';
import { BackPill } from './BackPill';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, Plus, Trash2, ArrowRight, Calendar as CalendarIcon, ListTodo, Flame, Sparkles, Check } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { pingAuraFuse } from '../../utils/auraPing';
import { vibrate as nativeVibrate } from '../../utils/haptics';

/* ═══════════════════════════════════════════════════════════════════
   THE DESTINY CODEX — Galactic Navigation Engine
   All-SVG landscape map. No PNG/image assets — every gem, the crown,
   the compass, and the alchemical ornaments are drawn from scratch
   so the whole codex matches the parchment aesthetic and fits the
   user's viewport in one page (no scroll needed).
   ═══════════════════════════════════════════════════════════════════ */

const STORAGE_KEY = 'destiny_codex_state_v1';
const HOLD_MS = 1500;

/* Per-step gem palette — used to colour the SVG-drawn faceted gems. */
const GEM_COLORS = [
  { id: 'ruby',      core: '#C0294A', light: '#FF6478', dark: '#5A0A18' },
  { id: 'amber',     core: '#E2A132', light: '#FFD27A', dark: '#5C3A05' },
  { id: 'sapphire',  core: '#3A78C8', light: '#A8D0FF', dark: '#0E2A55' },
  { id: 'emerald',   core: '#2EA664', light: '#9BF0BE', dark: '#0A3A20' },
  { id: 'amethyst',  core: '#8B5CF6', light: '#D6BFFF', dark: '#3A1F66' },
  { id: 'tanzanite', core: '#7858E8', light: '#C9B7FF', dark: '#2C1A66' },
  { id: 'pearl',     core: '#E8E2D8', light: '#FFFFFF', dark: '#807866' },
  { id: 'topaz',     core: '#F0C040', light: '#FFE699', dark: '#705310' },
];

const NAVY    = '#050507';
const NAVY_HI = '#0E0E14';
const GOLD    = '#D4AF37';
const GOLD_HI = '#FFE9A8';
const GOLD_LO = '#7A5418';
const PAPER   = '#050507';

const uid = () => `${Date.now().toString(36)}_${Math.random().toString(36).slice(2,8)}`;

const loadState = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { destinies: [] };
    const s = JSON.parse(raw);
    return { destinies: Array.isArray(s.destinies) ? s.destinies : [] };
  } catch (_) { return { destinies: [] }; }
};
const saveState = (s) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch (_) {}
};

const haptic = (ms = 12) => {
  try { nativeVibrate(ms); } catch (_) {}
};
const startAuraRumble = (ref) => {
  let beat = 0;
  ref.current = window.setInterval(() => {
    beat += 1;
    try { nativeVibrate(8 + beat * 5); } catch (_) {}
  }, 250);
};
const stopAuraRumble = (ref) => {
  if (ref.current) { clearInterval(ref.current); ref.current = null; }
};
const fuseThud = () => {
  try { nativeVibrate([55, 35, 90, 30, 60]); } catch (_) {}
};

/* ───────────────────────── Destiny Icon (Try-tile) ───────────────────── */
export const DestinyTileIcon = ({ size = 26 }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" style={{ filter: 'drop-shadow(0 0 6px rgba(241,210,122,0.65))' }}>
    <defs>
      <radialGradient id="dstObs" cx="35%" cy="32%" r="65%">
        <stop offset="0%" stopColor="#3a3550"/>
        <stop offset="55%" stopColor="#0a0612"/>
        <stop offset="100%" stopColor="#000"/>
      </radialGradient>
      <linearGradient id="dstBrass" x1="0" x2="1" y1="0" y2="1">
        <stop offset="0%" stopColor="#FFF1B8"/>
        <stop offset="50%" stopColor="#B7892D"/>
        <stop offset="100%" stopColor="#5A3F10"/>
      </linearGradient>
    </defs>
    <circle cx="20" cy="20" r="13" fill="url(#dstObs)"/>
    <ellipse cx="20" cy="20" rx="17" ry="6.5" fill="none" stroke="url(#dstBrass)" strokeWidth="1.4" transform="rotate(-22 20 20)"/>
    <ellipse cx="20" cy="20" rx="17" ry="6.5" fill="none" stroke="url(#dstBrass)" strokeWidth="1.4" transform="rotate(28 20 20)"/>
    <ellipse cx="20" cy="20" rx="16.5" ry="16.5" fill="none" stroke="url(#dstBrass)" strokeWidth="1" opacity="0.7"/>
    <circle cx="20" cy="20" r="3" fill="#F1D27A"/>
    <circle cx="20" cy="20" r="1.4" fill="#FFF7C8"/>
  </svg>
);

/* ───────────────────────── Shared SVG defs (used inside the map) ─────── */
const MapDefs = () => (
  <defs>
    {/* Serpent gold gradient (across stroke) */}
    <linearGradient id="serpDark" x1="0" x2="1" y1="0" y2="0">
      <stop offset="0%"  stopColor="#2A1A08"/>
      <stop offset="50%" stopColor="#3A2410"/>
      <stop offset="100%" stopColor="#2A1A08"/>
    </linearGradient>
    <linearGradient id="serpGold" x1="0" x2="0" y1="0" y2="1">
      <stop offset="0%"  stopColor="#FFE9A8"/>
      <stop offset="40%" stopColor={GOLD}/>
      <stop offset="100%" stopColor={GOLD_LO}/>
    </linearGradient>
    <linearGradient id="serpInner" x1="0" x2="0" y1="0" y2="1">
      <stop offset="0%"  stopColor={GOLD_HI}/>
      <stop offset="100%" stopColor={GOLD}/>
    </linearGradient>
    {/* Crown gold gradient */}
    <linearGradient id="crownGold" x1="0" x2="0" y1="0" y2="1">
      <stop offset="0%"  stopColor="#FFEBA8"/>
      <stop offset="30%" stopColor={GOLD_HI}/>
      <stop offset="70%" stopColor={GOLD}/>
      <stop offset="100%" stopColor={GOLD_LO}/>
    </linearGradient>
    {/* Crown band gradient (3D bevel) */}
    <linearGradient id="crownBand" x1="0" x2="0" y1="0" y2="1">
      <stop offset="0%"   stopColor="#FFE8A4"/>
      <stop offset="30%"  stopColor={GOLD_HI}/>
      <stop offset="55%"  stopColor={GOLD}/>
      <stop offset="80%"  stopColor={GOLD_LO}/>
      <stop offset="100%" stopColor="#3A2410"/>
    </linearGradient>
    <linearGradient id="crownHi" x1="0" x2="0" y1="0" y2="1">
      <stop offset="0%"  stopColor="#FFFAE0" stopOpacity="0.95"/>
      <stop offset="100%" stopColor={GOLD_HI} stopOpacity="0"/>
    </linearGradient>
    <radialGradient id="velvetCap" cx="50%" cy="40%" r="60%">
      <stop offset="0%"  stopColor="#3A0F22"/>
      <stop offset="60%" stopColor="#1A0512"/>
      <stop offset="100%" stopColor="#080208"/>
    </radialGradient>
    <radialGradient id="crownHalo" cx="50%" cy="50%" r="50%">
      <stop offset="0%"  stopColor={GOLD_HI} stopOpacity="0.95"/>
      <stop offset="40%" stopColor={GOLD}    stopOpacity="0.45"/>
      <stop offset="100%" stopColor={GOLD}   stopOpacity="0"/>
    </radialGradient>
    {/* Jewel radial gradients (give jewels real dimension) */}
    {[
      ['jewRuby', '#FFC9D0', '#C0294A', '#3A0810'],
      ['jewSapp', '#BAE0FF', '#3A78C8', '#0A1A40'],
      ['jewEmer', '#B8FFD6', '#2EA664', '#062818'],
      ['jewAmet', '#E0CFFF', '#8B5CF6', '#26124A'],
      ['jewTanz', '#D6C9FF', '#7858E8', '#1E0F4D'],
      ['jewPearl','#FFFFFF', '#E8E2D8', '#807866'],
      ['jewTopaz','#FFEFB6', '#F0C040', '#5A4008'],
      ['jewAmber','#FFD27A', '#E2A132', '#3A2105'],
    ].map(([id, hi, core, dark]) => (
      <radialGradient key={id} id={id} cx="35%" cy="30%" r="65%">
        <stop offset="0%"  stopColor={hi}/>
        <stop offset="40%" stopColor={core}/>
        <stop offset="100%" stopColor={dark}/>
      </radialGradient>
    ))}
    {/* Per-step gem radial gradients (glassmorphic: hue blooms from
       lower portion of the orb so the top reads as cold dark glass) */}
    {GEM_COLORS.map((g) => (
      <radialGradient key={`grad-${g.id}`} id={`gem-${g.id}`} cx="50%" cy="65%" r="60%">
        <stop offset="0%"   stopColor={g.core}   stopOpacity="0.95"/>
        <stop offset="55%"  stopColor={g.dark}   stopOpacity="0.85"/>
        <stop offset="100%" stopColor="#020203"  stopOpacity="0.95"/>
      </radialGradient>
    ))}
    {/* Subtle top-light overlay gradient applied to every orb */}
    <radialGradient id="orbTopLight" cx="50%" cy="20%" r="60%">
      <stop offset="0%"  stopColor="rgba(255,255,255,0.35)"/>
      <stop offset="55%" stopColor="rgba(255,255,255,0.05)"/>
      <stop offset="100%" stopColor="rgba(255,255,255,0)"/>
    </radialGradient>
    {/* Fused gold gem gradient */}
    <radialGradient id="gemFused" cx="50%" cy="40%" r="65%">
      <stop offset="0%"  stopColor="#FFFAE0"/>
      <stop offset="50%" stopColor={GOLD_HI}/>
      <stop offset="100%" stopColor={GOLD_LO}/>
    </radialGradient>
    {/* Obsidian sheen for the parchment plate */}
    <linearGradient id="obsidianSheen" x1="0" x2="1" y1="0" y2="1">
      <stop offset="0%"   stopColor="#0A0A12"/>
      <stop offset="50%"  stopColor="#050507"/>
      <stop offset="100%" stopColor="#0A0608"/>
    </linearGradient>
    {/* Radial vignette darkening the edges */}
    <radialGradient id="vignette" cx="50%" cy="50%" r="65%">
      <stop offset="0%"  stopColor="rgba(0,0,0,0)"/>
      <stop offset="70%" stopColor="rgba(0,0,0,0.25)"/>
      <stop offset="100%" stopColor="rgba(0,0,0,0.65)"/>
    </radialGradient>
    {/* Brushed-stone grain noise (heavier than paper) */}
    <filter id="stoneGrain">
      <feTurbulence type="fractalNoise" baseFrequency="0.65" numOctaves="3"/>
      <feColorMatrix values="0 0 0 0 0.05  0 0 0 0 0.05  0 0 0 0 0.07  0 0 0 0.5 0"/>
    </filter>
    {/* Soft paper grain (lighter) */}
    <filter id="paperGrain">
      <feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="2"/>
      <feColorMatrix values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.14 0"/>
    </filter>
    {/* Soft gold glow */}
    <filter id="goldGlow" x="-50%" y="-50%" width="200%" height="200%">
      <feGaussianBlur stdDeviation="2.5"/>
    </filter>
    {/* Strong premium glow (for crown halo / fused gems) */}
    <filter id="premiumGlow" x="-100%" y="-100%" width="300%" height="300%">
      <feGaussianBlur stdDeviation="4"/>
    </filter>
  </defs>
);

/* ───────────────────────── Obsidian texture + corners (dynamic) ─────── */
const ParchmentCard = memo(({ totalH = 1700 }) => {
  const innerH = totalH - 12;
  const innerH2 = totalH - 28;
  return (
    <g>
      <rect x="6" y="6" width="588" height={innerH} rx="14" ry="14"
        fill="#020203" stroke="url(#serpGold)" strokeWidth="1.2"/>
      <rect x="6" y="6" width="588" height={innerH} rx="14" ry="14"
        fill="url(#obsidianSheen)" opacity="0.85"/>
      <rect x="6" y="6" width="588" height={innerH} rx="14" ry="14"
        filter="url(#stoneGrain)" opacity="0.45"/>
      <rect x="6" y="6" width="588" height={innerH} rx="14" ry="14"
        fill="url(#vignette)"/>
      <rect x="14" y="14" width="572" height={innerH2} rx="10" ry="10"
        fill="none" stroke={GOLD} strokeWidth="0.4" strokeDasharray="2 5" opacity="0.4"/>
      {[
        { x: 6,   y: 6,           r: 0   },
        { x: 594, y: 6,           r: 90  },
        { x: 594, y: totalH - 6,  r: 180 },
        { x: 6,   y: totalH - 6,  r: 270 },
      ].map((c, i) => (
        <g key={i} transform={`translate(${c.x} ${c.y}) rotate(${c.r})`}>
          <path d="M 0 0 L 80 0 L 80 9 L 20 9 L 20 70 L 11 70 L 7 74 L 0 74 Z"
            fill="url(#crownGold)" stroke="#3A2410" strokeWidth="0.3" opacity="0.95"/>
          <circle cx="7" cy="7" r="2.4" fill={GOLD_HI}/>
          <path d="M 28 16 L 42 16 L 42 21 L 28 21 Z" fill={GOLD_HI} opacity="0.5"/>
          <path d="M 14 34 L 18 34 L 18 58 L 14 58 Z" fill={GOLD_HI} opacity="0.4"/>
        </g>
      ))}
      {/* Constellation dots scattered evenly along the length */}
      {Array.from({ length: Math.ceil(totalH / 220) }).map((_, row) => {
        const yBase = 180 + row * 200;
        return [
          [60, yBase],
          [540, yBase + 70],
          [70, yBase + 140],
          [530, yBase + 30],
        ].map(([x, y], i) => (
          <g key={`${row}-${i}`}>
            <circle cx={x} cy={y} r="1" fill={GOLD_HI} opacity="0.42"/>
            <circle cx={x + 9} cy={y + 4} r="0.6" fill={GOLD_HI} opacity="0.28"/>
            <line x1={x} y1={y} x2={x + 9} y2={y + 4}
              stroke={GOLD} strokeWidth="0.25" strokeDasharray="1 2" opacity="0.28"/>
          </g>
        ));
      })}
    </g>
  );
});

/* ───────────────────────── Decorative ornaments (dynamic) ────────────── */
const Ornaments = memo(({ totalH = 1700 }) => {
  // Position major ornaments at proportional points along the column.
  const y1 = Math.min(580, totalH * 0.34);
  const y2 = Math.min(totalH - 600, totalH * 0.65);
  const yPentagram = totalH - 160;
  const yMoon      = totalH - 140;
  return (
    <g opacity="0.85">
      {/* Big alchemy wheel — left (rotates clockwise) */}
      <g transform={`translate(85 ${y1})`} opacity="0.34">
        <g>
          <animateTransform attributeName="transform" type="rotate"
            from="0 0 0" to="360 0 0" dur="48s" repeatCount="indefinite"/>
          <circle r="55" fill="none" stroke={GOLD} strokeWidth="0.5"/>
          <circle r="42" fill="none" stroke={GOLD} strokeWidth="0.4" strokeDasharray="2 3"/>
          <circle r="28" fill="none" stroke={GOLD} strokeWidth="0.4"/>
          {Array.from({ length: 12 }).map((_, i) => {
            const a = (i * 30) * Math.PI / 180;
            return <line key={i} x1={Math.cos(a)*42} y1={Math.sin(a)*42} x2={Math.cos(a)*55} y2={Math.sin(a)*55} stroke={GOLD} strokeWidth="0.5"/>;
          })}
          {Array.from({ length: 16 }).map((_, i) => (
            <rect key={i} x="-1" y="-60" width="2" height="5" fill={GOLD} transform={`rotate(${i*22.5})`}/>
          ))}
        </g>
      </g>
      {/* Alchemy wheel — right (rotates counter-clockwise, slower for parallax) */}
      <g transform={`translate(515 ${y2})`} opacity="0.34">
        <g>
          <animateTransform attributeName="transform" type="rotate"
            from="360 0 0" to="0 0 0" dur="64s" repeatCount="indefinite"/>
          <circle r="55" fill="none" stroke={GOLD} strokeWidth="0.5"/>
          <circle r="42" fill="none" stroke={GOLD} strokeWidth="0.4" strokeDasharray="2 3"/>
          <circle r="28" fill="none" stroke={GOLD} strokeWidth="0.4"/>
          {Array.from({ length: 16 }).map((_, i) => (
            <rect key={i} x="-1" y="-60" width="2" height="5" fill={GOLD} transform={`rotate(${i*22.5})`}/>
          ))}
        </g>
      </g>
      {/* Pentagram bottom-left (slow rotate) */}
      <g transform={`translate(70 ${yPentagram})`} opacity="0.6">
        <g>
          <animateTransform attributeName="transform" type="rotate"
            from="0 0 0" to="360 0 0" dur="90s" repeatCount="indefinite"/>
          <circle r="26" fill="none" stroke={GOLD} strokeWidth="0.6"/>
          <circle r="20" fill="none" stroke={GOLD} strokeWidth="0.3" strokeDasharray="1 2"/>
          {Array.from({ length: 5 }).map((_, i) => {
            const a1 = (i * 72 - 90) * Math.PI / 180;
            const a2 = ((i + 2) * 72 - 90) * Math.PI / 180;
            return <line key={i}
              x1={Math.cos(a1)*22} y1={Math.sin(a1)*22}
              x2={Math.cos(a2)*22} y2={Math.sin(a2)*22}
              stroke={GOLD} strokeWidth="0.6"/>;
          })}
        </g>
      </g>
      {/* Saturn glyph above title (subtle ring orbit) */}
      <g transform="translate(300 90)" opacity="0.85">
        <g>
          <animateTransform attributeName="transform" type="rotate"
            from="0 0 0" to="360 0 0" dur="36s" repeatCount="indefinite"/>
          <ellipse rx="16" ry="3.6" fill="none" stroke={GOLD} strokeWidth="1.2" transform="rotate(-22)"/>
        </g>
        <circle r="7.5" fill={NAVY_HI} stroke={GOLD} strokeWidth="1"/>
        <circle r="3.5" fill={GOLD} opacity="0.5"/>
      </g>
      {/* Crescent moon — bottom-right */}
      <g transform={`translate(530 ${yMoon})`} opacity="0.55">
        <path d="M 0 -14 A 14 14 0 1 0 0 14 A 11 14 0 1 1 0 -14 Z" fill={GOLD}/>
      </g>
    </g>
  );
});

/* ───────────────────────── Compass rose (SVG) ────────────────────────── */
const CompassRose = memo(({ cx, cy, size = 60 }) => (
  <g transform={`translate(${cx} ${cy})`}>
    <circle r={size} fill={NAVY_HI} stroke={GOLD} strokeWidth="1.2" opacity="0.9"/>
    <circle r={size * 0.78} fill="none" stroke={GOLD} strokeWidth="0.4" strokeDasharray="2 3"/>
    {/* Rotating directional star — smooth continuous loop */}
    <g>
      <animateTransform attributeName="transform" type="rotate"
        from="0 0 0" to="360 0 0" dur="32s" repeatCount="indefinite"/>
      <path d={`M 0 ${-size*0.85} L ${size*0.08} 0 L 0 ${size*0.85} L ${-size*0.08} 0 Z`} fill="url(#crownGold)"/>
      <path d={`M ${-size*0.85} 0 L 0 ${-size*0.08} L ${size*0.85} 0 L 0 ${size*0.08} Z`} fill="url(#crownGold)" opacity="0.7"/>
      <path d={`M ${-size*0.55} ${-size*0.55} L 0 0 L ${size*0.55} ${size*0.55} L 0 0 Z`} fill={GOLD} opacity="0.4"/>
      <path d={`M ${size*0.55}  ${-size*0.55} L 0 0 L ${-size*0.55} ${size*0.55} L 0 0 Z`} fill={GOLD} opacity="0.4"/>
    </g>
    <circle r="3" fill={GOLD_HI}/>
    {['N','E','S','W'].map((c, i) => {
      const a = (i * 90) * Math.PI / 180;
      const r = size * 0.92;
      return (
        <text key={c} x={Math.sin(a) * r} y={-Math.cos(a) * r + 2.5} textAnchor="middle"
          fontSize="7" fontFamily="Outfit, sans-serif" fill={GOLD_HI} opacity="0.9">{c}</text>
      );
    })}
  </g>
));

/* ───────────────────────── Crown of Ascension (Premium 3D) ──────────── */
const CrownOfAscension = memo(({ cx, cy, progress, flashKey, flashColor }) => {
  const fullVictory = progress >= 1;
  const brightness  = 0.7 + progress * 0.35;
  return (
    <g transform={`translate(${cx} ${cy})`}>
      {/* ===== Eternal gold breath halo (idle baseline, never stops) ===== */}
      <circle r="60" fill="url(#crownHalo)" opacity="0.35">
        <animate attributeName="opacity" values="0.22;0.55;0.22" dur="3.6s" repeatCount="indefinite"/>
        <animate attributeName="r"       values="55;62;55"       dur="3.6s" repeatCount="indefinite"/>
      </circle>
      <circle r="44" fill="none" stroke={GOLD_HI} strokeWidth="0.5" opacity="0.4">
        <animate attributeName="opacity" values="0.25;0.7;0.25" dur="3.6s" repeatCount="indefinite"/>
      </circle>

      {/* ===== Fuse flash burst — colored, fires every fuse ===== */}
      {flashKey > 0 && (
        <g key={flashKey}>
          <circle r="58" fill={flashColor} opacity="0" filter="url(#premiumGlow)">
            <animate attributeName="opacity" values="0;0.95;0.55;0.25;0" dur="1.4s" fill="freeze"/>
            <animate attributeName="r"       values="38;64;68;62;58" dur="1.4s" fill="freeze"/>
          </circle>
          <circle r="0" fill={flashColor} opacity="0.85" filter="url(#premiumGlow)">
            <animate attributeName="r"       values="0;55;82" dur="1.1s" fill="freeze"/>
            <animate attributeName="opacity" values="0.85;0.4;0" dur="1.1s" fill="freeze"/>
          </circle>
          <circle r="0" fill="none" stroke={GOLD_HI} strokeWidth="1.8">
            <animate attributeName="r"       values="6;52;76" dur="1.1s" fill="freeze"/>
            <animate attributeName="opacity" values="1;0.5;0" dur="1.1s" fill="freeze"/>
          </circle>
        </g>
      )}

      <g style={{ filter: `brightness(${brightness})` }}>
        {/* Velvet cap behind */}
        <path d="M -32 14 Q 0 4 32 14 L 28 26 L -28 26 Z" fill="url(#velvetCap)"/>
        <path d="M -28 14 Q 0 8 28 14" fill="none" stroke="rgba(0,0,0,0.6)" strokeWidth="0.6"/>

        {/* ===== Bevelled main band (layered for 3D depth) ===== */}
        <path d="M -34 4 L 34 4 L 30 26 L -30 26 Z" fill={GOLD_LO}/>
        <path d="M -33 5 L 33 5 L 29 25 L -29 25 Z" fill="url(#crownBand)"/>
        <path d="M -33 5 L 33 5 L 31 9 L -31 9 Z" fill="url(#crownHi)"/>
        <path d="M -30 22 L 30 22 L 28 25 L -28 25 Z" fill="rgba(0,0,0,0.45)"/>
        <path d="M -33 5.4 L 33 5.4" fill="none" stroke="#FFFCE5" strokeWidth="0.5" opacity="0.7"/>

        {/* Refined acanthus filigree (engraved, monochrome) */}
        <path d="M -28 16 C -24 11 -20 11 -16 16 C -12 21 -8 21 -4 16"
          fill="none" stroke="#3A2410" strokeWidth="0.5" opacity="0.85"/>
        <path d="M 4 16 C 8 11 12 11 16 16 C 20 21 24 21 28 16"
          fill="none" stroke="#3A2410" strokeWidth="0.5" opacity="0.85"/>
        <path d="M -28 16 C -24 11 -20 11 -16 16 C -12 21 -8 21 -4 16"
          fill="none" stroke={GOLD_HI} strokeWidth="0.25" opacity="0.55"
          transform="translate(0 -0.5)"/>
        <path d="M 4 16 C 8 11 12 11 16 16 C 20 21 24 21 28 16"
          fill="none" stroke={GOLD_HI} strokeWidth="0.25" opacity="0.55"
          transform="translate(0 -0.5)"/>

        {/* Seed pearl row — uniform pearls, lightly highlighted */}
        {[-24, -18, -12, -6, 0, 6, 12, 18, 24].map((bx) => (
          <g key={bx}>
            <circle cx={bx} cy="20" r="1.1" fill="#F5EDD8"/>
            <circle cx={bx - 0.25} cy="19.6" r="0.4" fill="#FFFFFF" opacity="0.95"/>
          </g>
        ))}

        {/* ===== Three rising spires with proper 3D bevels ===== */}
        {/* LEFT spire */}
        <path d="M -34 4 L -22 -26 L -10 4 Z" fill="url(#crownBand)"/>
        <path d="M -22 -26 L -10 4 L -16 4 Z" fill={GOLD_LO} opacity="0.9"/>
        <path d="M -34 4 L -22 -26 L -28 -4 Z" fill="url(#crownHi)" opacity="0.55"/>
        <path d="M -22 -26 L -22 4" stroke="#3A2410" strokeWidth="0.35" opacity="0.7"/>

        {/* MIDDLE spire (tallest) */}
        <path d="M -10 4 L 0 -46 L 10 4 Z" fill="url(#crownBand)"/>
        <path d="M 0 -46 L 10 4 L 3 4 Z" fill={GOLD_LO} opacity="0.9"/>
        <path d="M -10 4 L 0 -46 L -3 -14 Z" fill="url(#crownHi)" opacity="0.7"/>
        <path d="M 0 -46 L 0 4" stroke="#3A2410" strokeWidth="0.35" opacity="0.7"/>

        {/* RIGHT spire */}
        <path d="M 10 4 L 22 -26 L 34 4 Z" fill="url(#crownBand)"/>
        <path d="M 22 -26 L 34 4 L 28 4 Z" fill={GOLD_LO} opacity="0.9"/>
        <path d="M 10 4 L 22 -26 L 16 -4 Z" fill="url(#crownHi)" opacity="0.55"/>
        <path d="M 22 -26 L 22 4" stroke="#3A2410" strokeWidth="0.35" opacity="0.7"/>

        {/* Refined arches between spires (double line: gold + shadow) */}
        <path d="M -10 4 Q -5 -8 0 -8 Q 5 -8 10 4"
          fill="none" stroke={GOLD_LO} strokeWidth="0.9" opacity="0.95"/>
        <path d="M -10 4 Q -5 -7 0 -7 Q 5 -7 10 4"
          fill="none" stroke={GOLD_HI} strokeWidth="0.35" opacity="0.7"/>
        <path d="M -32 4 Q -27 -2 -22 -4"
          fill="none" stroke={GOLD_LO} strokeWidth="0.7" opacity="0.85"/>
        <path d="M 22 -4 Q 27 -2 32 4"
          fill="none" stroke={GOLD_LO} strokeWidth="0.7" opacity="0.85"/>
      </g>

      {/* ===== Single regal central ruby — the ONLY coloured stone ===== */}
      <g>
        <ellipse cx="0" cy="14" rx="3.6" ry="4.4" fill="url(#jewRuby)"/>
        <ellipse cx="0" cy="14" rx="3.6" ry="4.4" fill="none" stroke="rgba(0,0,0,0.55)" strokeWidth="0.5"/>
        <ellipse cx="-0.9" cy="12.3" rx="1.1" ry="0.65" fill="#FFC9D0" opacity="0.95"/>
        <ellipse cx="-0.9" cy="12.2" rx="0.45" ry="0.28" fill="#FFFFFF"/>
      </g>

      {/* ===== Fleur-de-lis finials atop each spire (all gold, no garish jewels) ===== */}
      {[{ x: -22, ty: -26 }, { x: 22, ty: -26 }].map((s, idx) => (
        <g key={idx} transform={`translate(${s.x} ${s.ty})`}>
          {/* Cross-pattée style finial */}
          <path d="M 0 -8 L 2 -3 L 5 -2 L 2 0 L 0 5 L -2 0 L -5 -2 L -2 -3 Z"
            fill="url(#crownBand)" stroke="#3A2410" strokeWidth="0.3"/>
          <circle cx="0" cy="-1" r="0.9" fill={GOLD_HI}/>
        </g>
      ))}

      {/* Summit cabochon: large refined ruby with halo */}
      <g>
        <circle cx="0" cy="-46" r="8.5" fill="url(#crownHalo)" opacity="0.85" filter="url(#goldGlow)"/>
        <circle cx="0" cy="-46" r="5.8" fill="url(#jewRuby)"/>
        <circle cx="0" cy="-46" r="5.8" fill="none" stroke="rgba(0,0,0,0.6)" strokeWidth="0.55"/>
        {/* Brilliant-cut facet seams */}
        <path d="M -5.8 -46 L 5.8 -46 M 0 -51.8 L 0 -40.2 M -4.1 -50.1 L 4.1 -41.9 M 4.1 -50.1 L -4.1 -41.9"
          stroke="rgba(0,0,0,0.3)" strokeWidth="0.35"/>
        <ellipse cx="-1.5" cy="-48" rx="1.8" ry="1.1" fill="#FFFFFF" opacity="0.92"/>
        <ellipse cx="-1.5" cy="-48" rx="0.7" ry="0.4" fill="#FFFFFF"/>
      </g>

      {/* Crown summit — fleur-de-lis finial above the central jewel */}
      <g>
        <path d="M -4 -54 Q 0 -62 4 -54 Q 2 -57 0 -57 Q -2 -57 -4 -54 Z"
          fill="url(#crownBand)" stroke="#3A2410" strokeWidth="0.3"/>
        <path d="M 0 -62 L 0 -70" stroke={GOLD_HI} strokeWidth="1.2"/>
        <circle cx="0" cy="-70" r="1.6" fill={GOLD_HI}>
          <animate attributeName="opacity" values="0.6;1;0.6" dur="3.6s" repeatCount="indefinite"/>
        </circle>
      </g>

      {/* Light rays (progress-aware) — radiate from a slightly higher centre. */}
      {progress >= 0.4 && Array.from({ length: 14 }).map((_, i) => {
        const a = (-90 + i * (360 / 14)) * Math.PI / 180;
        const r1 = 56, r2 = 56 + 10 + progress * 18;
        return (
          <line key={i}
            x1={Math.cos(a) * r1} y1={Math.sin(a) * r1 - 8}
            x2={Math.cos(a) * r2} y2={Math.sin(a) * r2 - 8}
            stroke={GOLD_HI} strokeWidth="0.55" opacity={0.22 + progress * 0.55}/>
        );
      })}

      {/* Sparkle ring on full victory */}
      {fullVictory && Array.from({ length: 12 }).map((_, i) => {
        const a = (i * 30 - 90) * Math.PI / 180;
        return (
          <circle key={i} cx={Math.cos(a) * 50} cy={Math.sin(a) * 50} r="1.6" fill={GOLD_HI}>
            <animate attributeName="opacity" values="0;1;0" dur="2.4s" begin={`${i*0.18}s`} repeatCount="indefinite"/>
            <animate attributeName="r"       values="1;2.8;1" dur="2.4s" begin={`${i*0.18}s`} repeatCount="indefinite"/>
          </circle>
        );
      })}
    </g>
  );
});

/* ───────────────────────── Profile Ball (user-photo gem) ─────────────── */
const ProfileBall = memo(({ cx, cy, r, picture, fallbackLetter, ringColor, index, isInitiation }) => {
  const clipId = `clip-prof-${index}-${cx}-${cy}`;
  return (
    <g pointerEvents="none">
      <defs>
        <clipPath id={clipId}>
          <circle cx={cx} cy={cy} r={r - 1.4}/>
        </clipPath>
      </defs>
      {/* Outer glow — ring's color */}
      <circle cx={cx} cy={cy} r={r + 5} fill={ringColor} opacity="0.35" filter="url(#premiumGlow)"/>
      {/* Ring (colored) */}
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={ringColor} strokeWidth="2.4"
        style={{ filter: `drop-shadow(0 0 4px ${ringColor})` }}/>
      {/* Inner highlight ring */}
      <circle cx={cx} cy={cy} r={r - 1.4} fill="rgba(15,15,15,0.85)"/>
      {/* User picture (or fallback letter) */}
      {picture ? (
        <image
          href={picture}
          x={cx - (r - 1.4)} y={cy - (r - 1.4)}
          width={(r - 1.4) * 2} height={(r - 1.4) * 2}
          preserveAspectRatio="xMidYMid slice"
          clipPath={`url(#${clipId})`}
        />
      ) : (
        <text x={cx} y={cy + r * 0.32} textAnchor="middle"
          fontSize={r * 1.1} fontFamily="Cinzel, serif" fontWeight="700"
          fill={ringColor}>{fallbackLetter}</text>
      )}
      {/* Crisp white rim */}
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(255,255,255,0.22)" strokeWidth="0.6"/>
      {/* Step number badge (skipped for Initiation) */}
      {!isInitiation && (
        <>
          <circle cx={cx + r * 0.95} cy={cy - r * 0.85} r="7" fill={ringColor} stroke={GOLD} strokeWidth="0.7"/>
          <text x={cx + r * 0.95} y={cy - r * 0.85 + 2.5} textAnchor="middle"
            fontSize="7.5" fontFamily="JetBrains Mono, monospace" fontWeight="700"
            fill="#0A0A12">{index + 1}</text>
        </>
      )}
    </g>
  );
});

/* ───────────────────────── Premium ORB gem (3D sphere) ──────────────── */
const SvgGem = memo(({ cx, cy, r, color, fused, index, onConquer }) => {
  const [progress, setProgress] = useState(0);
  const startRef  = useRef(0);
  const rafRef    = useRef(null);
  const rumbleRef = useRef(null);
  const doneRef   = useRef(false);

  const stop = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    stopAuraRumble(rumbleRef);
    setProgress(0);
    doneRef.current = false;
  }, []);

  const start = useCallback((e) => {
    if (fused) return;
    if (e && e.preventDefault) { try { e.preventDefault(); } catch (_) {} }
    startRef.current = performance.now();
    doneRef.current = false;
    haptic(12);
    startAuraRumble(rumbleRef);
    const tick = (t) => {
      const dt = t - startRef.current;
      const p = Math.min(1, dt / HOLD_MS);
      setProgress(p);
      if (p >= 1) {
        if (!doneRef.current) {
          doneRef.current = true;
          stopAuraRumble(rumbleRef);
          fuseThud();
          onConquer && onConquer(color.core);
        }
        stop();
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [fused, onConquer, stop, color.core]);

  useEffect(() => () => stop(), [stop]);

  const circ = 2 * Math.PI * (r + 3);

  return (
    <g
      style={{ cursor: fused ? 'default' : 'pointer', touchAction: 'none' }}
      onPointerDown={start}
      onPointerUp={stop}
      onPointerLeave={stop}
      onPointerCancel={stop}
      data-testid={`gem-node-${fused ? 'completed' : 'pending'}-${index}`}
    >
      {/* Orbital fusion ring — slow-spinning dashed circle hints "tap me" */}
      {!fused && (
        <g style={{ transformOrigin: `${cx}px ${cy}px`, animation: 'destinyOrbit 8s linear infinite' }}>
          <circle cx={cx} cy={cy} r={r + 7} fill="none"
            stroke={color.core} strokeWidth="0.55" strokeDasharray="2 4" opacity="0.7"/>
          <circle cx={cx + r + 7} cy={cy} r="1.3" fill={color.light} opacity="0.95"/>
        </g>
      )}

      {/* Fusion charge — intensifies as the long-press progresses */}
      {progress > 0 && !fused && (
        <>
          <circle cx={cx} cy={cy} r={r + 8 + progress * 14} fill={color.core}
            opacity={0.2 + progress * 0.5} filter="url(#premiumGlow)"/>
          {Array.from({ length: 8 }).map((_, i) => {
            const a = (i * 45) * Math.PI / 180;
            const inner = r + 5, outer = r + 14 + progress * 12;
            return (
              <line key={i}
                x1={cx + Math.cos(a) * inner} y1={cy + Math.sin(a) * inner}
                x2={cx + Math.cos(a) * outer} y2={cy + Math.sin(a) * outer}
                stroke={color.light} strokeWidth="1" opacity={progress * 0.95}
                strokeLinecap="round"/>
            );
          })}
        </>
      )}

      {/* === Glassmorphic gemstone orb === */}
      {/* Outer halo glow (color bleeds into the dark canvas) */}
      <circle cx={cx} cy={cy} r={r + 5} fill={fused ? GOLD_HI : color.core}
        opacity={fused ? 0.5 : 0.22 + progress * 0.4} filter="url(#premiumGlow)"/>
      {/* Breathing pulse on pending gems — strong own-color in/out */}
      {!fused && (
        <>
          <circle cx={cx} cy={cy} r={r + 6} fill={color.core} opacity="0.25" filter="url(#premiumGlow)">
            <animate attributeName="opacity" values="0.15;0.55;0.15" dur="2.8s" repeatCount="indefinite"/>
            <animate attributeName="r"       values={`${r + 4};${r + 9};${r + 4}`} dur="2.8s" repeatCount="indefinite"/>
          </circle>
          <circle cx={cx} cy={cy} r={r + 2} fill="none" stroke={color.core} strokeWidth="0.6" opacity="0.7">
            <animate attributeName="r" values={`${r + 2};${r + 6};${r + 2}`} dur="2.8s" repeatCount="indefinite"/>
            <animate attributeName="opacity" values="0.7;0.15;0.7" dur="2.8s" repeatCount="indefinite"/>
          </circle>
        </>
      )}

      {/* GLASS BODY — dark translucent base with deep inset shadow.
         Always present.  When fused, the gold layer fades in over 1.5s. */}
      <circle cx={cx} cy={cy} r={r} fill="rgba(15,15,15,0.62)"/>
      <circle cx={cx} cy={cy} r={r} fill={`url(#gem-${color.id})`} opacity={fused ? 0 : 1}/>
      {/* Top-down light overlay — gives the glass a curved highlight */}
      <circle cx={cx} cy={cy} r={r} fill="url(#orbTopLight)" opacity="0.8"/>
      {/* Inset shadow ring — fakes box-shadow: inset 0 0 10px rgba(0,0,0,0.8) */}
      <circle cx={cx} cy={cy} r={r - 0.5} fill="none"
        stroke="rgba(0,0,0,0.85)" strokeWidth="1.4" opacity="0.85"/>
      {/* Crisp white rim border — like 1px solid rgba(255,255,255,0.1) */}
      <circle cx={cx} cy={cy} r={r + 0.5} fill="none"
        stroke="rgba(255,255,255,0.18)" strokeWidth="0.8"/>
      {/* Thin gold setting (very subtle, just a hint) */}
      <circle cx={cx} cy={cy} r={r + 2} fill="none"
        stroke={GOLD} strokeWidth="0.4" opacity={fused ? 1 : 0.4}/>

      {/* === Fused-gold transition layer — fades in as progress→1 over 1.5s,
         then locks at full opacity when fused = true === */}
      <g style={{
        opacity: fused ? 1 : progress,
        transition: 'opacity 0.2s ease-out',
        transformOrigin: `${cx}px ${cy}px`,
        transform: fused ? 'scale(1)' : `scale(${1 + progress * 0.18})`,
      }}>
        <circle cx={cx} cy={cy} r={r} fill="url(#gemFused)"
          style={{ filter: `drop-shadow(0 0 ${4 + progress * 10}px ${GOLD_HI})` }}/>
        {/* Gold inner ring */}
        <circle cx={cx} cy={cy} r={r - 1} fill="none" stroke={GOLD_HI}
          strokeWidth="0.6" opacity="0.85"/>
        {/* Top specular highlight */}
        <ellipse cx={cx - r * 0.34} cy={cy - r * 0.42}
          rx={r * 0.34} ry={r * 0.18}
          fill="#FFFFFF" opacity="0.78"
          transform={`rotate(-25 ${cx - r * 0.34} ${cy - r * 0.42})`}/>
        <circle cx={cx - r * 0.32} cy={cy - r * 0.4} r={r * 0.1} fill="#FFFFFF" opacity="0.95"/>
      </g>

      {/* Hold progress arc */}
      {progress > 0 && !fused && (
        <circle cx={cx} cy={cy} r={r + 4} fill="none" stroke={GOLD_HI} strokeWidth="2.2"
          strokeDasharray={`${progress * circ} ${circ}`}
          strokeLinecap="round"
          transform={`rotate(-90 ${cx} ${cy})`}
          style={{ filter: 'drop-shadow(0 0 5px rgba(255,233,168,0.95))' }}
        />
      )}

      {/* Numbered badge */}
      <circle cx={cx + r * 0.95} cy={cy - r * 0.85} r="7" fill={fused ? GOLD_HI : '#0A0A12'}
        stroke={GOLD} strokeWidth="0.7" opacity="0.95"/>
      <text x={cx + r * 0.95} y={cy - r * 0.85 + 2.5} textAnchor="middle"
        fontSize="7.5" fontFamily="JetBrains Mono, monospace" fontWeight="700"
        fill={fused ? GOLD_LO : GOLD_HI}>{index + 1}</text>
    </g>
  );
});

/* ───────────────────────── The Codex Map (portrait, dynamic) ────────── */
const CodexMap = memo(({ destiny, onConquer, flashKey, flashColor, userPicture, userName, fuseSparkKey, fuseSparkColor }) => {
  const steps = destiny.steps || [];
  const n = steps.length;
  const conqueredCount = steps.filter((s) => s.status === 'completed').length;
  const progress = n > 0 ? conqueredCount / n : 0;
  const titleText = (destiny.title || '').toUpperCase();
  const pathRef = useRef(null);
  const [positions, setPositions] = useState([]);

  /* Dynamic map height — grows with step count.  Hard cap at 25 steps
     (enforced by Manifestation Ledger).  BOTTOM_PAD is tight so the
     Initiation label sits close to the profile ball (no huge gap). */
  const TOP_PAD    = 480;   // space above the path (title + crown + ASCENSION + AIM)
  const BOTTOM_PAD = 110;   // tight space below (Initiation ball + label)
  const PER_STEP   = 90;
  const pathSpan   = Math.max(900, n * PER_STEP);
  const TOTAL_H    = TOP_PAD + pathSpan + BOTTOM_PAD;

  /* Path: tall vertical serpent. Starts just below the Crown (≈y=300)
     and winds down with horizontal S-curves to ≈y=1330 (Initiation).
     Built programmatically so we can lengthen it to match the number
     of milestones. */
  const pathD = useMemo(() => {
    const startY = TOP_PAD;
    const endY   = TOP_PAD + pathSpan;
    const span   = endY - startY;
    // 6 segments min, more for longer paths (every 8 steps add a sweep).
    const segments = Math.max(6, Math.ceil(n / 4));
    const segH = span / segments;
    const swing = 175;
    let d = `M 300 ${startY}`;
    for (let i = 0; i < segments; i += 1) {
      const direction = i % 2 === 0 ? 1 : -1;
      const sx = 300;
      const sy = startY + i * segH;
      const ey = startY + (i + 1) * segH;
      const c1x = sx + direction * swing;
      const c1y = sy + segH * 0.18;
      const c2x = sx + direction * swing;
      const c2y = sy + segH * 0.82;
      d += ` C ${c1x} ${c1y}, ${c2x} ${c2y}, 300 ${ey}`;
    }
    return d;
  }, [n, pathSpan, TOP_PAD]);

  // Distribute gems evenly along the path — REVERSED so step 1 lands at
  // the bottom (Initiation) and the final step lands at the top (just
  // below the Crown of Ascension).  The user climbs UP the path.
  useEffect(() => {
    if (!pathRef.current || n === 0) { setPositions([]); return; }
    const p = pathRef.current;
    let total = 0;
    try { total = p.getTotalLength(); } catch (_) { total = 1000; }
    const pad = total * 0.045;
    const usable = total - pad * 2;
    const out = [];
    for (let i = 0; i < n; i += 1) {
      // Reverse: the LAST step (i = n-1) is at the START of the path (top, near crown).
      // The FIRST step (i = 0) is at the END of the path (bottom, near initiation).
      const t = n === 1 ? total / 2 : pad + (usable * (n - 1 - i)) / (n - 1);
      try {
        const pt = p.getPointAtLength(t);
        out.push({ x: pt.x, y: pt.y });
      } catch (_) {
        out.push({ x: 300, y: 700 });
      }
    }
    setPositions(out);
  }, [n, pathD]);

  const gemR = n <= 6 ? 22 : n <= 10 ? 19 : n <= 14 ? 17 : 15;

  return (
    <svg viewBox={`0 0 600 ${TOTAL_H}`} width="100%" height="100%"
      preserveAspectRatio="xMidYMid meet" data-testid="codex-map">
      <MapDefs/>
      <ParchmentCard totalH={TOTAL_H}/>
      <Ornaments totalH={TOTAL_H}/>

      {/* Title — "THE CODEX OF [NAME]'S DESTINY".  Name + DESTINY rendered
         on the SAME line so an orphan apostrophe-S can never appear. */}
      <g transform="translate(300 150)" textAnchor="middle">
        <text fontSize="22" fontFamily="Cinzel, serif" fontWeight="700"
          fill={GOLD_HI} letterSpacing="5">THE CODEX OF</text>
        {userName && userName.length > 0 ? (
          <>
            <text y="42" fontSize="30" fontFamily="Cinzel, serif" fontWeight="700"
              fill={GOLD_HI} letterSpacing="5">
              {`${userName.toUpperCase().slice(0, 12)}\u2019S DESTINY`}
            </text>
            <line x1="-110" y1="60" x2="110" y2="60" stroke={GOLD} strokeWidth="0.5"/>
          </>
        ) : (
          <>
            <text y="42" fontSize="32" fontFamily="Cinzel, serif" fontWeight="700"
              fill={GOLD_HI} letterSpacing="8">DESTINY</text>
            <line x1="-90" y1="60" x2="90" y2="60" stroke={GOLD} strokeWidth="0.5"/>
          </>
        )}
      </g>

      {/* Crown — Ascension marker, sits below the title */}
      <CrownOfAscension
        cx={300} cy={328}
        progress={progress}
        flashKey={flashKey}
        flashColor={flashColor}
      />
      <text x="300" y="408" textAnchor="middle"
        fontSize="14" fontFamily="Cinzel, serif" fontWeight="500"
        fill={GOLD_HI} letterSpacing="6" opacity="0.95">ASCENSION</text>

      {/* Aim text — no "AIM" label, just the destiny title in Cinzel */}
      {titleText && (
        <g transform="translate(300 444)" textAnchor="middle" data-testid="destiny-title">
          <text fontSize="18" fontFamily="Cinzel, serif" fontWeight="500"
            fill={GOLD_HI} letterSpacing="2"
            style={{ textShadow: `0 0 ${4 + progress * 10}px ${GOLD_HI}` }}>
            {titleText.length > 28 ? `${titleText.slice(0, 27)}…` : titleText}
          </text>
          <g transform="translate(0 18)">
            <rect x="-65" y="0" width="130" height="3" rx="1.5" fill="rgba(255,255,255,0.06)"/>
            <rect x="-65" y="0" width={130 * progress} height="3" rx="1.5" fill={GOLD_HI}
              style={{ filter: `drop-shadow(0 0 4px ${GOLD_HI})` }}/>
            <text x="74" y="3" fontSize="8" fontFamily="JetBrains Mono, monospace"
              fill={GOLD_HI} letterSpacing="1">{conqueredCount}/{n}</text>
          </g>
        </g>
      )}

      {/* Delete button moved to the modal header (Nexus › Fit View › Delete). */}

      {/* Side annotation: Aural Alignment & Empirical Hierarchy are
         REPLACED by the per-gem note boxes rendered alongside the path. */}

      {/* Compass rose (mid right) */}
      <CompassRose cx={520} cy={780} size={48}/>

      {/* Serpent gold path — razor-thin metallic gold with outer glow */}
      <path ref={pathRef} d={pathD} fill="none" stroke="transparent"/>
      <path d={pathD} fill="none" stroke="#5A3F10" strokeWidth="6" strokeLinecap="round" opacity="0.5"/>
      <path d={pathD} fill="none" stroke={GOLD} strokeWidth="2.4" strokeLinecap="round" opacity="0.9"
        style={{ filter: 'drop-shadow(0 0 6px rgba(212,175,55,0.55))' }}/>
      <path d={pathD} fill="none" stroke={GOLD_HI} strokeWidth="0.7" strokeLinecap="round" opacity="0.85"/>
      {/* Travelling gold spark — ALWAYS climbs UP the wire from the
         Initiation (bottom) to the Crown (top).  The user is ascending,
         so the spark visualises that journey at all times. */}
      <circle r="5" fill={GOLD_HI} filter="url(#premiumGlow)" opacity="1">
        <animateMotion dur="9s" repeatCount="indefinite" rotate="auto"
          calcMode="linear" keyPoints="1;0" keyTimes="0;1" path={pathD}/>
        <animate attributeName="r" values="4;6.5;4" dur="1.4s" repeatCount="indefinite"/>
      </circle>
      <circle r="12" fill={GOLD_HI} opacity="0.45" filter="url(#premiumGlow)">
        <animateMotion dur="9s" repeatCount="indefinite"
          calcMode="linear" keyPoints="1;0" keyTimes="0;1" path={pathD}/>
      </circle>
      <circle r="20" fill={GOLD} opacity="0.18" filter="url(#premiumGlow)">
        <animateMotion dur="9s" repeatCount="indefinite"
          calcMode="linear" keyPoints="1;0" keyTimes="0;1" path={pathD}/>
      </circle>

      {/* Colored fuse spark — shoots UPWARD from fused gem to the crown.
         Re-fires every time fuseSparkKey changes.  We approximate "from gem
         to crown" by running a fast upward motion along the path from the
         end (bottom = initiation) to the start (top = crown). */}
      {/* Colored fuse spark — shoots UPWARD from fused gem along the path
         to the crown.  Big, bright, slow enough to be noticed. */}
      {fuseSparkKey > 0 && (
        <g key={`fuse-spark-${fuseSparkKey}`}>
          {/* Outer halo (huge soft bloom) */}
          <circle r="22" fill={fuseSparkColor} opacity="0" filter="url(#premiumGlow)">
            <animateMotion dur="1.8s" fill="freeze" calcMode="linear" keyPoints="1;0" keyTimes="0;1" path={pathD}/>
            <animate attributeName="opacity" values="0;0.5;0.45;0" keyTimes="0;0.15;0.85;1" dur="1.8s" fill="freeze"/>
          </circle>
          {/* Mid bright ring */}
          <circle r="12" fill={fuseSparkColor} opacity="0" filter="url(#premiumGlow)">
            <animateMotion dur="1.8s" fill="freeze" rotate="auto" calcMode="linear" keyPoints="1;0" keyTimes="0;1" path={pathD}/>
            <animate attributeName="opacity" values="0;0.95;0.85;0" keyTimes="0;0.1;0.85;1" dur="1.8s" fill="freeze"/>
            <animate attributeName="r"       values="6;14;14;10"     keyTimes="0;0.1;0.85;1" dur="1.8s" fill="freeze"/>
          </circle>
          {/* Bright core dot */}
          <circle r="6" fill="#FFFFFF" opacity="0" filter="url(#premiumGlow)">
            <animateMotion dur="1.8s" fill="freeze" calcMode="linear" keyPoints="1;0" keyTimes="0;1" path={pathD}/>
            <animate attributeName="opacity" values="0;1;0.9;0" keyTimes="0;0.1;0.85;1" dur="1.8s" fill="freeze"/>
          </circle>
          {/* Particle trail — 4 dots following */}
          {[0.10, 0.20, 0.32, 0.45].map((offset, idx) => (
            <circle key={idx} r={4 - idx * 0.6} fill={fuseSparkColor} opacity="0" filter="url(#premiumGlow)">
              <animateMotion dur={`${1.8 + offset * 0.8}s`} fill="freeze" calcMode="linear" keyPoints="1;0" keyTimes="0;1" path={pathD}/>
              <animate attributeName="opacity" values={`0;${0.85 - idx * 0.15};0`}
                keyTimes={`0;${0.15 + offset};1`} dur={`${1.8 + offset * 0.8}s`} fill="freeze"/>
            </circle>
          ))}
        </g>
      )}

      {/* Initiation label — sits ABOVE the profile ball, tight to the path end. */}
      <text x="300" y={TOP_PAD + pathSpan + 26} textAnchor="middle"
        fontSize="14" fontFamily="Cinzel, serif" fontWeight="500"
        fill={GOLD_HI} letterSpacing="6" opacity="0.95">INITIATION</text>

      {/* Gems — fused steps render as a user-profile ball instead. */}
      {positions.map((p, i) => {
        const fusedHere = steps[i]?.status === 'completed';
        const c = GEM_COLORS[i % GEM_COLORS.length];
        if (fusedHere && userPicture) {
          return (
            <ProfileBall
              key={i}
              cx={p.x} cy={p.y} r={gemR}
              picture={userPicture}
              fallbackLetter={(userName || '?').charAt(0).toUpperCase()}
              ringColor={c.core}
              index={i}
            />
          );
        }
        return (
          <SvgGem
            key={i}
            cx={p.x} cy={p.y} r={gemR}
            color={c}
            fused={fusedHere}
            index={i}
            onConquer={(col) => onConquer(i, col)}
          />
        );
      })}

      {/* Initiation profile ball — sits at the very BOTTOM of the wire as
         the user's starting point, immediately below the INITIATION label. */}
      {n > 0 && (
        <ProfileBall
          cx={300} cy={TOP_PAD + pathSpan + 70} r={gemR}
          picture={userPicture}
          fallbackLetter={(userName || '?').charAt(0).toUpperCase()}
          ringColor={GOLD}
          index={-1}
          isInitiation
        />
      )}

      {/* Per-gem note boxes — pushed FURTHER from the path so they never
         touch other gems.  Premium glassmorphic look with double border. */}
      {positions.map((p, i) => {
        const onRight = p.x < 290;
        const boxW = 130, boxH = 38;
        const horizGap = 110;  // distance from gem centre to box edge
        const boxX = onRight
          ? Math.min(594 - boxW - 8, p.x + horizGap)
          : Math.max(8, p.x - horizGap - boxW);
        const boxY = Math.max(TOP_PAD - 5, Math.min(TOP_PAD + pathSpan - boxH + 5, p.y - boxH / 2));
        const fusedNote = steps[i]?.status === 'completed';
        const txt = steps[i]?.step || '';
        const truncated = txt.length > 18 ? `${txt.slice(0, 17)}…` : txt;
        const connectorX1 = onRight ? p.x + gemR : p.x - gemR;
        const connectorX2 = onRight ? boxX : boxX + boxW;
        return (
          <g key={`note-${i}`} pointerEvents="none">
            {/* Connector — dot at gem, dash line, small dot at box */}
            <circle cx={connectorX1} cy={p.y} r="1.4" fill={GOLD} opacity="0.7"/>
            <line
              x1={connectorX1} y1={p.y}
              x2={connectorX2} y2={boxY + boxH / 2}
              stroke={GOLD} strokeWidth="0.55" opacity="0.55" strokeDasharray="3 3"
            />
            <circle cx={connectorX2} cy={boxY + boxH / 2} r="1.4" fill={GOLD} opacity="0.85"/>
            {/* Glassmorphic outer shadow */}
            <rect x={boxX - 1} y={boxY + 1} width={boxW + 2} height={boxH} rx="4"
              fill="rgba(0,0,0,0.55)"/>
            {/* Box body */}
            <rect x={boxX} y={boxY} width={boxW} height={boxH} rx="3.5"
              fill="rgba(10,10,14,0.92)"/>
            {/* White hairline rim */}
            <rect x={boxX} y={boxY} width={boxW} height={boxH} rx="3.5"
              fill="none" stroke="rgba(255,255,255,0.16)" strokeWidth="0.7"/>
            {/* Inset dark hairline */}
            <rect x={boxX + 0.6} y={boxY + 0.6} width={boxW - 1.2} height={boxH - 1.2} rx="3"
              fill="none" stroke="rgba(0,0,0,0.9)" strokeWidth="0.6"/>
            {/* Gold double-line border */}
            <rect x={boxX + 3} y={boxY + 3} width={boxW - 6} height={boxH - 6} rx="2.4"
              fill="none" stroke={GOLD} strokeWidth="0.4" opacity={fusedNote ? 0.85 : 0.55}/>
            <rect x={boxX + 4.5} y={boxY + 4.5} width={boxW - 9} height={boxH - 9} rx="2"
              fill="none" stroke={GOLD} strokeWidth="0.2" opacity="0.35"/>
            {/* Corner accents */}
            {[[boxX + 5, boxY + 5], [boxX + boxW - 5, boxY + 5], [boxX + 5, boxY + boxH - 5], [boxX + boxW - 5, boxY + boxH - 5]].map(([px, py], idx) => (
              <circle key={idx} cx={px} cy={py} r="1.1" fill={GOLD_HI} opacity={fusedNote ? 0.95 : 0.6}/>
            ))}
            {/* Tag */}
            <text x={boxX + 9} y={boxY + 13}
              fontSize="6.8" fontFamily="JetBrains Mono, monospace"
              fill={fusedNote ? GOLD_HI : GOLD} opacity="0.92" letterSpacing="2.2">
              STEP {String(i + 1).padStart(2, '0')}
            </text>
            {/* Step text */}
            <text x={boxX + boxW / 2} y={boxY + 29} textAnchor="middle"
              fontSize="10" fontFamily="Cinzel, serif" fontWeight="500"
              fill={fusedNote ? GOLD_HI : '#E8E2D8'}
              opacity={fusedNote ? 1 : 0.95}
              letterSpacing="0.8">
              {truncated || '—'}
            </text>
            {/* Completion seal dot (fused only) */}
            {fusedNote && (
              <g>
                <circle cx={boxX + boxW - 11} cy={boxY + 10} r="3.2" fill={GOLD_HI}
                  style={{ filter: `drop-shadow(0 0 5px ${GOLD_HI})` }}/>
                <circle cx={boxX + boxW - 11} cy={boxY + 10} r="1.6" fill={GOLD}/>
              </g>
            )}
          </g>
        );
      })}

      {/* Legend removed per user request. */}
    </svg>
  );
});

/* ───────────────────────── Path-of-Conquest wrapper (scroll + fit) ───── */
const PathOfConquest = memo(({ destiny, onConquer, onDelete, flashKey, flashColor, userPicture, userName, fuseSparkKey, fuseSparkColor }) => {
  const steps = destiny.steps || [];
  const n = steps.length;
  const overallProgress = n > 0 ? steps.filter((s) => s.status === 'completed').length / n : 0;

  // Aspect ratio of the SVG depends on step count (taller for more steps).
  const totalH = 480 + Math.max(900, n * 90) + 200;
  const aspectStr = `600 / ${totalH}`;

  return (
    <div className="relative w-full h-full" data-testid="path-of-conquest">
      {/* Scrollable map — single mode (Fit Full View removed by design). */}
      {(
        <div className="absolute inset-0 overflow-y-auto overflow-x-hidden" data-testid="destiny-scroll-area">
          <div className="w-full" style={{
            aspectRatio: aspectStr,
            minHeight: 'calc(100dvh - 90px)',
          }}>
            <CodexMap
              destiny={destiny}
              onConquer={onConquer}
              flashKey={flashKey}
              flashColor={flashColor}
              userPicture={userPicture}
              userName={userName}
              fuseSparkKey={fuseSparkKey}
              fuseSparkColor={fuseSparkColor}
            />
          </div>
          {/* Footer hint inside scroll */}
          <div className="text-center py-3">
            {overallProgress < 1 ? (
              <p className="font-['Cormorant_Garamond'] italic text-[11px]"
                style={{ color: 'rgba(224,181,97,0.7)' }}>
                Press &amp; hold any gem 1.5 s to fuse it. Conquer in any order.
              </p>
            ) : (
              <motion.p
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                className="font-['Cormorant_Garamond'] text-sm"
                style={{ color: GOLD_HI, letterSpacing: '0.18em', textShadow: `0 0 14px ${GOLD_HI}` }}
              >
                ✦ Destiny fulfilled. The Crown shines for you. ✦
              </motion.p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}); 

/* ───────────────────────── Manifestation Ledger (create form) ────────── */
const ManifestationLedger = ({ onBack, onCreate }) => {
  const [aim, setAim] = useState('');
  const [milestones, setMilestones] = useState(['']);
  const [converting, setConverting] = useState(false);

  const update = (i, v) => setMilestones((a) => a.map((m, idx) => idx === i ? v : m));
  const add    = () => setMilestones((a) => (a.length < 25 ? [...a, ''] : a));
  const remove = (i) => setMilestones((a) => (a.length > 1 ? a.filter((_, idx) => idx !== i) : a));
  const canConvert = aim.trim().length > 1 && milestones.filter((m) => m.trim()).length >= 1;

  const trigger = () => {
    if (!canConvert || converting) return;
    setConverting(true);
    haptic(120);
    setTimeout(() => {
      const cleanSteps = milestones.map((m) => m.trim()).filter(Boolean).slice(0, 25);
      const steps = cleanSteps.map((step, i) => ({
        step,
        status: 'pending',
        gem: GEM_COLORS[i % GEM_COLORS.length].id,
      }));
      onCreate({
        id: uid(),
        title: aim.trim(),
        currentStepIndex: 0,
        steps,
        createdAt: new Date().toISOString(),
      });
    }, 1100);
  };

  return (
    <div className="relative z-10 px-5"
      style={{ paddingBottom: 'calc(56px + var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)))', boxSizing: 'border-box' }}
      data-testid="manifestation-ledger">
      <div className="pt-3 pb-4 flex items-center gap-2">
        <button type="button" onClick={onBack}
          className="w-9 h-9 rounded-full flex items-center justify-center transition-colors"
          aria-label="Back" data-testid="ledger-back"
          style={{ border: `1px solid ${GOLD}40`, color: GOLD }}>
          <ChevronLeft size={16}/>
        </button>
        <div>
          <p className="font-['Outfit'] text-[10px] uppercase tracking-[0.3em]" style={{ color: GOLD }}>Manifestation Ledger</p>
          <h2 className="font-['Cormorant_Garamond'] text-xl leading-tight" style={{ color: GOLD_HI }}>Forge a New Destiny</h2>
        </div>
      </div>

      <label className="block">
        <span className="font-['Outfit'] text-[10px] uppercase tracking-[0.25em]" style={{ color: `${GOLD}AA` }}>The Aim — your destination</span>
        <input value={aim} onChange={(e) => setAim(e.target.value)}
          placeholder="e.g. Become a Certified Commercial Pilot"
          className="w-full mt-1 bg-transparent outline-none font-['Cormorant_Garamond'] text-lg py-2"
          style={{ color: GOLD_HI, borderBottom: `1px solid ${GOLD}55` }}
          data-testid="ledger-aim" maxLength={120}/>
      </label>

      <div className="mt-6">
        <span className="font-['Outfit'] text-[10px] uppercase tracking-[0.25em]" style={{ color: `${GOLD}AA` }}>The Milestones — list any order</span>
        <div className="mt-2 space-y-2">
          {milestones.map((m, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full flex items-center justify-center font-['Outfit'] text-[10px]"
                style={{ color: GOLD, border: `1px solid ${GOLD}55` }}>{i + 1}</div>
              <input value={m} onChange={(e) => update(i, e.target.value)}
                placeholder={i === 0 ? 'Ground School Exam' : `Milestone ${i + 1}`}
                className="flex-1 outline-none rounded-lg px-3 py-2 font-['Outfit'] text-sm"
                style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${GOLD}33`, color: GOLD_HI }}
                data-testid={`ledger-milestone-${i}`} maxLength={140}/>
              {milestones.length > 1 && (
                <button type="button" onClick={() => remove(i)}
                  className="w-7 h-7 rounded-full flex items-center justify-center"
                  style={{ color: 'rgba(255,100,120,0.6)' }} aria-label="Remove milestone">
                  <Trash2 size={13}/>
                </button>
              )}
            </div>
          ))}
        </div>
        <button type="button" onClick={add} disabled={milestones.length >= 25}
          className="mt-3 flex items-center gap-2 px-3 py-1.5 rounded-full font-['Outfit'] text-[11px] uppercase tracking-[0.18em] disabled:opacity-30"
          style={{ border: `1px solid ${GOLD}40`, color: GOLD }}
          data-testid="ledger-add-milestone">
          <Plus size={12}/> {milestones.length >= 25 ? 'Maximum 25 reached' : 'Add Milestone'}
        </button>
      </div>

      <div className="mt-8 flex flex-col items-center">
        <button type="button" onClick={trigger} disabled={!canConvert || converting}
          className="relative px-8 py-3 rounded-full font-['Outfit'] uppercase tracking-[0.25em] text-xs font-semibold transition-all disabled:opacity-30"
          style={{
            background: `linear-gradient(135deg, ${GOLD}22, ${GOLD_LO}11)`,
            border: `1.5px solid ${GOLD}`,
            color: GOLD_HI,
            boxShadow: canConvert ? `0 0 22px ${GOLD}AA, inset 0 0 14px ${GOLD}33` : 'none',
            letterSpacing: '0.3em',
          }}
          data-testid="ledger-convert">
          {converting ? 'Manifesting…' : 'Convert to Destiny Map'}
          <ArrowRight size={14} className="inline ml-2 align-middle"/>
        </button>
        <p className="font-['Cormorant_Garamond'] italic text-[12px] mt-3" style={{ color: `${GOLD}88` }}>
          The path will be forged from your words alone.
        </p>
      </div>

      <AnimatePresence>
        {converting && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[120] flex items-center justify-center pointer-events-none"
            style={{ background: 'radial-gradient(ellipse at center, rgba(241,210,122,0.16), rgba(0,0,0,0.55))' }}>
            {Array.from({ length: 14 }).map((_, i) => (
              <motion.div key={i}
                initial={{ scaleY: 0, opacity: 0 }}
                animate={{ scaleY: [0, 1, 0], opacity: [0, 1, 0] }}
                transition={{ duration: 0.9, delay: i * 0.04, ease: 'easeOut' }}
                style={{
                  position: 'absolute', width: 2, height: '46%',
                  background: `linear-gradient(180deg, rgba(255,241,184,0), ${GOLD_HI}, rgba(255,241,184,0))`,
                  transformOrigin: 'center',
                  transform: `rotate(${(i / 14) * 360}deg) translateY(-22%)`,
                  filter: `blur(0.6px) drop-shadow(0 0 6px ${GOLD_HI})`,
                }}/>
            ))}
            <motion.div initial={{ scale: 0.4, opacity: 0 }}
              animate={{ scale: [0.4, 1.4, 1], opacity: [0, 1, 0] }}
              transition={{ duration: 1.05, ease: [0.16, 1, 0.3, 1] }}
              style={{
                width: 120, height: 120, borderRadius: '50%',
                background: `radial-gradient(circle, ${GOLD_HI}, ${GOLD} 40%, transparent 70%)`,
                filter: 'blur(2px)',
              }}/>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   DESTINY SCHEDULE — user creates day-by-day plan (Day 1: walking, …)
   ═══════════════════════════════════════════════════════════════════ */
const SCHEDULE_PERIODS = [
  { days: 7,   label: '7-Day Sprint' },
  { days: 14,  label: '14-Day Path' },
  { days: 21,  label: '21-Day Habit' },
  { days: 30,  label: '30-Day Arc' },
  { days: 66,  label: '66-Day Forge' },
  { days: 100, label: '100-Day Crown' },
];

const DestinyScheduleTab = ({ destiny, onSaveSchedule, onResetCycle }) => {
  const initialDays = destiny.scheduleDays || 30;
  const [days, setDays] = useState(initialDays);
  const [rows, setRows] = useState(() => {
    const existing = destiny.schedule || [];
    const arr = Array.from({ length: initialDays }, (_, i) => {
      const found = existing.find((r) => r.day === i + 1);
      return { day: i + 1, task: found ? found.task : '' };
    });
    return arr;
  });
  const [savedFlash, setSavedFlash] = useState(false);

  useEffect(() => {
    // Re-sync rows when the destiny id or its stored schedule changes.
    const existing = destiny.schedule || [];
    setDays(destiny.scheduleDays || 30);
    setRows(Array.from({ length: destiny.scheduleDays || 30 }, (_, i) => {
      const found = existing.find((r) => r.day === i + 1);
      return { day: i + 1, task: found ? found.task : '' };
    }));
  }, [destiny.id]);

  const changePeriod = (newDays) => {
    setDays(newDays);
    setRows((prev) => {
      const arr = Array.from({ length: newDays }, (_, i) => {
        const found = prev.find((r) => r.day === i + 1);
        return { day: i + 1, task: found ? found.task : '' };
      });
      return arr;
    });
    haptic(15);
  };

  const updateRow = (idx, val) => {
    setRows((prev) => prev.map((r, i) => i === idx ? { ...r, task: val } : r));
  };

  const clearAll = () => {
    if (!window.confirm('Clear every day\'s task on this schedule?')) return;
    setRows((prev) => prev.map((r) => ({ ...r, task: '' })));
  };

  const save = () => {
    const cleaned = rows.map((r) => ({ day: r.day, task: (r.task || '').trim() }));
    onSaveSchedule(cleaned, days);
    haptic(40);
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 1400);
  };

  const filledCount = rows.filter((r) => r.task.trim().length > 0).length;
  const fusedCount  = Object.keys(destiny.fusedSteps || {}).length;
  const cycleComplete = fusedCount >= days && days > 0;

  return (
    <div className="relative z-10 px-5 pt-3 pb-24" data-testid="destiny-schedule-tab">
      <p className="font-['Outfit'] text-[10px] uppercase tracking-[0.32em]" style={{ color: GOLD }}>Destiny Schedule</p>
      <h3 className="font-['Cormorant_Garamond'] text-xl leading-tight" style={{ color: GOLD_HI }}>{destiny.title}</h3>
      <p className="font-['Cormorant_Garamond'] italic text-sm mt-1" style={{ color: `${GOLD}88` }}>
        Draft each day — walk, talk, build. Fusing happens inside the Step tab.
      </p>

      {/* Period picker */}
      <div className="mt-4">
        <span className="font-['Outfit'] text-[10px] uppercase tracking-[0.25em]" style={{ color: `${GOLD}AA` }}>Cycle Length</span>
        <div className="mt-2 flex flex-wrap gap-2">
          {SCHEDULE_PERIODS.map((p) => {
            const active = p.days === days;
            return (
              <button key={p.days} type="button" onClick={() => changePeriod(p.days)}
                className="px-3 py-1.5 rounded-full font-['Outfit'] text-[11px] uppercase tracking-[0.18em] transition-all"
                style={{
                  background: active ? `linear-gradient(135deg, ${GOLD_HI}, ${GOLD_LO})` : 'transparent',
                  border: `1px solid ${active ? GOLD_HI : GOLD}55`,
                  color: active ? '#050507' : GOLD_HI,
                  boxShadow: active ? `0 0 12px ${GOLD}AA` : 'none',
                }}
                data-testid={`schedule-period-${p.days}`}>
                {p.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Progress strip */}
      <div className="mt-4 grid grid-cols-3 gap-2 text-center">
        <div className="p-2 rounded-lg" style={{ border: `1px solid ${GOLD}33`, background: `${NAVY_HI}` }}>
          <div className="font-['Outfit'] text-[9px] uppercase tracking-[0.2em]" style={{ color: `${GOLD}AA` }}>Filled</div>
          <div className="font-['Cormorant_Garamond'] text-lg" style={{ color: GOLD_HI }}>{filledCount} / {days}</div>
        </div>
        <div className="p-2 rounded-lg" style={{ border: `1px solid ${GOLD}33`, background: `${NAVY_HI}` }}>
          <div className="font-['Outfit'] text-[9px] uppercase tracking-[0.2em]" style={{ color: `${GOLD}AA` }}>Fused</div>
          <div className="font-['Cormorant_Garamond'] text-lg" style={{ color: GOLD_HI }}>{fusedCount} / {days}</div>
        </div>
        <div className="p-2 rounded-lg" style={{ border: `1px solid ${GOLD}33`, background: `${NAVY_HI}` }}>
          <div className="font-['Outfit'] text-[9px] uppercase tracking-[0.2em]" style={{ color: `${GOLD}AA` }}>Status</div>
          <div className="font-['Cormorant_Garamond'] text-sm mt-1" style={{ color: cycleComplete ? GOLD_HI : `${GOLD}AA` }}>
            {cycleComplete ? 'Complete ✦' : 'Active'}
          </div>
        </div>
      </div>

      {/* Rows */}
      <div className="mt-5 space-y-2">
        {rows.map((r, i) => {
          const isFused = !!(destiny.fusedSteps && destiny.fusedSteps[r.day]);
          return (
            <div key={r.day} className="flex items-center gap-2">
              <div className="w-11 flex-shrink-0 rounded-lg py-1.5 text-center font-['Outfit'] text-[10px] uppercase tracking-[0.15em]"
                style={{
                  color: isFused ? '#050507' : GOLD_HI,
                  background: isFused ? `linear-gradient(135deg, ${GOLD_HI}, ${GOLD_LO})` : 'transparent',
                  border: `1px solid ${GOLD}55`,
                }}>
                D{r.day}
              </div>
              <input
                value={r.task}
                onChange={(e) => updateRow(i, e.target.value)}
                placeholder={`e.g. ${['Walk 30 min','Call one mentor','Ship a draft','Read 20 pages','Deep-work session'][i % 5]}`}
                className="flex-1 outline-none rounded-lg px-3 py-2 font-['Outfit'] text-sm"
                style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${GOLD}33`, color: GOLD_HI }}
                maxLength={140}
                data-testid={`schedule-day-input-${r.day}`}
              />
              {isFused && (
                <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: `${GOLD}22`, border: `1px solid ${GOLD}` }} aria-label="Fused">
                  <Check size={12} color={GOLD_HI}/>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Actions */}
      <div className="mt-6 flex flex-wrap items-center gap-2">
        <button type="button" onClick={save}
          className="px-5 py-2 rounded-full font-['Outfit'] uppercase text-[11px] tracking-[0.22em] transition-all"
          style={{
            background: `linear-gradient(135deg, ${GOLD_HI}, ${GOLD_LO})`,
            color: '#050507',
            boxShadow: `0 0 14px ${GOLD}88`,
          }}
          data-testid="schedule-save">
          {savedFlash ? 'Saved ✓' : 'Save Schedule'}
        </button>
        <button type="button" onClick={clearAll}
          className="px-4 py-2 rounded-full font-['Outfit'] uppercase text-[11px] tracking-[0.2em]"
          style={{ border: `1px solid ${GOLD}55`, color: `${GOLD}CC` }}
          data-testid="schedule-clear">
          Clear All
        </button>
        {cycleComplete && (
          <button type="button" onClick={onResetCycle}
            className="px-4 py-2 rounded-full font-['Outfit'] uppercase text-[11px] tracking-[0.2em] flex items-center gap-1.5"
            style={{ border: `1px solid ${GOLD_HI}`, color: GOLD_HI, background: `${GOLD}18` }}
            data-testid="schedule-reset-cycle">
            <Sparkles size={12}/> Reset Cycle
          </button>
        )}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   DESTINY STEP — calendar view; click a day to log & FUSE
   ═══════════════════════════════════════════════════════════════════ */
const DestinyStepTab = ({ destiny, onFuseDay, onResetCycle }) => {
  const schedule = destiny.schedule || [];
  const days = destiny.scheduleDays || 0;
  const fused = destiny.fusedSteps || {};

  const [openDay, setOpenDay] = useState(null); // { day, task }
  const [entry, setEntry]     = useState('');
  const [fusing, setFusing]   = useState(false);

  useEffect(() => {
    if (openDay) {
      const existing = fused[openDay.day];
      setEntry(existing ? existing.text : '');
    } else {
      setEntry('');
    }
  }, [openDay, fused]);

  const totalFused = Object.keys(fused).length;
  const cycleComplete = days > 0 && totalFused >= days;

  const openCell = (day, task) => {
    setOpenDay({ day, task });
    haptic(12);
  };
  const closeCell = () => { setOpenDay(null); setEntry(''); };

  const doFuse = () => {
    if (!openDay || !entry.trim() || fusing) return;
    setFusing(true);
    fuseThud();
    setTimeout(() => {
      onFuseDay(openDay.day, entry.trim());
      setFusing(false);
      setOpenDay(null);
      setEntry('');
    }, 650);
  };

  if (days === 0 || schedule.length === 0) {
    return (
      <div className="relative z-10 px-5 pt-8 pb-24 text-center" data-testid="destiny-step-empty">
        <p className="font-['Outfit'] text-[10px] uppercase tracking-[0.32em]" style={{ color: GOLD }}>Destiny Step</p>
        <h3 className="font-['Cormorant_Garamond'] text-xl mt-2" style={{ color: GOLD_HI }}>No Schedule Yet</h3>
        <p className="font-['Cormorant_Garamond'] italic text-sm mt-3 max-w-sm mx-auto" style={{ color: `${GOLD}88` }}>
          Head to the <b style={{ color: GOLD_HI }}>Schedule</b> tab, draft your Day 1 → Day {SCHEDULE_PERIODS[3].days} plan, then return here to fuse each day one step at a time.
        </p>
      </div>
    );
  }

  return (
    <div className="relative z-10 px-5 pt-3 pb-24" data-testid="destiny-step-tab">
      <p className="font-['Outfit'] text-[10px] uppercase tracking-[0.32em]" style={{ color: GOLD }}>Destiny Step</p>
      <h3 className="font-['Cormorant_Garamond'] text-xl leading-tight" style={{ color: GOLD_HI }}>{destiny.title}</h3>
      <p className="font-['Cormorant_Garamond'] italic text-sm mt-1" style={{ color: `${GOLD}88` }}>
        Tap any day → log what you did → Fuse. Fused days lock into the schedule.
      </p>

      <div className="mt-3 flex items-center gap-3">
        <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
          <div className="h-full rounded-full" style={{
            width: `${Math.round((totalFused / days) * 100)}%`,
            background: `linear-gradient(90deg, ${GOLD_LO}, ${GOLD}, ${GOLD_HI})`,
            boxShadow: `0 0 8px ${GOLD}AA`,
          }}/>
        </div>
        <span className="font-['Outfit'] text-[11px]" style={{ color: GOLD_HI }}>{totalFused} / {days}</span>
      </div>

      {/* Calendar grid */}
      <div className="mt-5 grid grid-cols-5 sm:grid-cols-7 gap-2" data-testid="destiny-step-grid">
        {schedule.map((r) => {
          const isFused = !!fused[r.day];
          const hasTask = (r.task || '').trim().length > 0;
          return (
            <button key={r.day} type="button" onClick={() => openCell(r.day, r.task)}
              className="relative aspect-square rounded-xl p-1.5 flex flex-col text-left overflow-hidden transition-transform active:scale-[0.97]"
              style={{
                background: isFused
                  ? `linear-gradient(155deg, ${GOLD_HI}, ${GOLD_LO})`
                  : `linear-gradient(160deg, ${NAVY_HI}, ${NAVY})`,
                border: `1px solid ${isFused ? GOLD_HI : `${GOLD}44`}`,
                boxShadow: isFused ? `0 0 14px ${GOLD}88, inset 0 0 10px rgba(255,255,255,0.25)` : 'none',
                color: isFused ? '#050507' : GOLD_HI,
              }}
              data-testid={`step-day-${r.day}`}>
              <div className="flex items-center justify-between">
                <span className="font-['Outfit'] text-[9px] uppercase tracking-[0.18em]"
                  style={{ opacity: 0.8 }}>D{r.day}</span>
                {isFused && (
                  <span className="w-4 h-4 rounded-full flex items-center justify-center"
                    style={{ background: '#050507' }}>
                    <Flame size={9} color={GOLD_HI}/>
                  </span>
                )}
              </div>
              <p className="mt-1 font-['Cormorant_Garamond'] text-[11px] leading-tight line-clamp-3"
                style={{ opacity: hasTask ? 1 : 0.4 }}>
                {hasTask ? r.task : '—'}
              </p>
            </button>
          );
        })}
      </div>

      {cycleComplete && (
        <div className="mt-6 rounded-xl p-4 text-center"
          style={{ border: `1px solid ${GOLD_HI}`, background: `${GOLD}15`, boxShadow: `0 0 22px ${GOLD}55` }}
          data-testid="step-cycle-complete">
          <p className="font-['Cormorant_Garamond'] text-lg" style={{ color: GOLD_HI }}>Cycle Complete ✦</p>
          <p className="font-['Cormorant_Garamond'] italic text-sm mt-1" style={{ color: `${GOLD}CC` }}>
            Every day fused. Reset to run the next cycle.
          </p>
          <button type="button" onClick={onResetCycle}
            className="mt-3 px-4 py-2 rounded-full font-['Outfit'] uppercase text-[11px] tracking-[0.22em]"
            style={{
              background: `linear-gradient(135deg, ${GOLD_HI}, ${GOLD_LO})`,
              color: '#050507', boxShadow: `0 0 14px ${GOLD}AA`,
            }}
            data-testid="step-reset-cycle">
            Reset Cycle
          </button>
        </div>
      )}

      {/* Fuse modal */}
      <AnimatePresence>
        {openDay && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[130] flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(4px)' }}
            onClick={closeCell}
            data-testid="fuse-modal">
            <motion.div
              initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-md rounded-2xl p-5"
              style={{
                background: `linear-gradient(165deg, ${NAVY_HI}, ${NAVY})`,
                border: `1px solid ${GOLD}77`,
                boxShadow: `0 0 30px ${GOLD}44, inset 0 0 10px rgba(255,255,255,0.04)`,
              }}>
              <div>
                <p className="font-['Outfit'] text-[10px] uppercase tracking-[0.28em]" style={{ color: GOLD }}>
                  Day {openDay.day} {fused[openDay.day] ? '• Fused' : ''}
                </p>
                <h4 className="font-['Cormorant_Garamond'] text-lg leading-tight mt-0.5" style={{ color: GOLD_HI }}>
                  {openDay.task || 'Untitled step'}
                </h4>
              </div>

              <label className="block mt-4">
                <span className="font-['Outfit'] text-[10px] uppercase tracking-[0.24em]" style={{ color: `${GOLD}AA` }}>
                  What did you do today for this dream?
                </span>
                <textarea value={entry} onChange={(e) => setEntry(e.target.value)}
                  rows={4}
                  placeholder="Write the exact step you took — the honest one."
                  className="w-full mt-2 outline-none rounded-lg px-3 py-2 font-['Cormorant_Garamond'] text-sm resize-none"
                  style={{ background: 'rgba(255,255,255,0.04)', border: `1px solid ${GOLD}33`, color: GOLD_HI }}
                  data-testid="fuse-entry"
                  maxLength={800}/>
              </label>

              {fused[openDay.day] && (
                <p className="mt-2 font-['Cormorant_Garamond'] italic text-[11px]" style={{ color: `${GOLD}88` }}>
                  This day is already fused — editing then re-fusing will overwrite.
                </p>
              )}

              <div className="mt-4 flex items-center gap-2 justify-end">
                <button type="button" onClick={closeCell}
                  className="px-4 py-2 rounded-full font-['Outfit'] uppercase text-[11px] tracking-[0.2em]"
                  style={{ border: `1px solid ${GOLD}55`, color: `${GOLD}CC` }}
                  data-testid="fuse-cancel">
                  Cancel
                </button>
                <button type="button" onClick={doFuse}
                  disabled={!entry.trim() || fusing}
                  className="px-5 py-2 rounded-full font-['Outfit'] uppercase text-[11px] tracking-[0.24em] disabled:opacity-40 flex items-center gap-1.5"
                  style={{
                    background: `linear-gradient(135deg, ${GOLD_HI}, ${GOLD_LO})`,
                    color: '#050507',
                    boxShadow: entry.trim() ? `0 0 14px ${GOLD}AA` : 'none',
                  }}
                  data-testid="fuse-confirm">
                  <Flame size={12}/> {fusing ? 'Fusing…' : (fused[openDay.day] ? 'Re-Fuse' : 'Fuse')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   MASTER TAB SHELL — Map / Schedule / Step for a single destiny
   ═══════════════════════════════════════════════════════════════════ */
const MasterTabBar = ({ subTab, onChange }) => {
  const tabs = [
    { id: 'map',      label: 'Map',      Icon: Sparkles },
    { id: 'schedule', label: 'Schedule', Icon: ListTodo },
    { id: 'step',     label: 'Step',     Icon: CalendarIcon },
  ];
  return (
    <div className="px-4 pt-2 pb-2 flex-shrink-0 z-10"
      style={{ borderBottom: `1px solid ${GOLD}22`, background: `${NAVY}CC` }}
      data-testid="destiny-master-tabbar">
      <div className="flex items-center gap-1.5">
        {tabs.map((t) => {
          const active = subTab === t.id;
          return (
            <button key={t.id} type="button" onClick={() => onChange(t.id)}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-full font-['Outfit'] text-[10px] uppercase tracking-[0.22em] transition-all"
              style={{
                background: active ? `linear-gradient(135deg, ${GOLD_HI}, ${GOLD_LO})` : 'transparent',
                border: `1px solid ${active ? GOLD_HI : `${GOLD}44`}`,
                color: active ? '#050507' : GOLD_HI,
                boxShadow: active ? `0 0 12px ${GOLD}77` : 'none',
              }}
              data-testid={`destiny-subtab-${t.id}`}>
              <t.Icon size={12}/> {t.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};


/* ───────────────────────── Nexus of Fates (hub) ──────────────────────── */
const NexusCardGem = ({ idx }) => {
  // Tiny faceted gem (SVG) for nexus campaign cards.
  const color = GEM_COLORS[idx % GEM_COLORS.length];
  const r = 14;
  const pts = [];
  for (let i = 0; i < 6; i += 1) {
    const a = (-90 + i * 60) * Math.PI / 180;
    pts.push(`${Math.cos(a) * r},${Math.sin(a) * r}`);
  }
  return (
    <svg width="36" height="36" viewBox="-20 -20 40 40">
      <circle r={r + 2} fill="none" stroke={GOLD} strokeWidth="1"/>
      <polygon points={pts.join(' ')} fill={color.core} stroke={color.dark} strokeWidth="0.8"
        style={{ filter: `drop-shadow(0 0 4px ${color.core})` }}/>
      <polygon points={pts.map((p) => p.split(',').map((v) => +v * 0.55).join(',')).join(' ')}
        fill={color.light} opacity="0.45"/>
    </svg>
  );
};
const NexusCrownIcon = () => (
  <svg width="36" height="36" viewBox="-20 -20 40 40">
    <path d="M -14 4 L 14 4 L 12 11 L -12 11 Z" fill="url(#crownGold)" stroke="#3A2410" strokeWidth="0.5"/>
    <path d="M -14 4 L -8 -8 L -2 4 L 0 -14 L 2 4 L 8 -8 L 14 4 Z" fill="url(#crownGold)" stroke="#3A2410" strokeWidth="0.5"/>
    <circle cx="0" cy="-14" r="2.2" fill="#C0294A"/>
    <circle cx="-8" cy="-8" r="1.5" fill="#3A78C8"/>
    <circle cx="8"  cy="-8" r="1.5" fill="#2EA664"/>
    <defs>
      <linearGradient id="crownGold" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0%"  stopColor={GOLD_HI}/>
        <stop offset="100%" stopColor={GOLD_LO}/>
      </linearGradient>
    </defs>
  </svg>
);

const NexusOfFates = ({ destinies, onOpenDestiny, onNewDestiny }) => (
  <div className="relative z-10 px-5 pt-3"
    style={{ paddingBottom: 'calc(56px + var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)))', boxSizing: 'border-box' }}
    data-testid="nexus-of-fates">
    <p className="font-['Outfit'] text-[10px] uppercase tracking-[0.35em]" style={{ color: GOLD }}>Nexus of Fates</p>
    <h2 className="font-['Cormorant_Garamond'] text-2xl leading-tight" style={{ color: GOLD_HI }}>Your Active Destinies</h2>
    <p className="font-['Cormorant_Garamond'] italic text-sm mt-1" style={{ color: `${GOLD}80` }}>
      Every constellation here is a life you are forging.
    </p>

    {/* Forge New Destiny — pinned at top so it never gets pushed down as maps accumulate */}
    <button type="button" onClick={onNewDestiny}
      className="mt-4 w-full relative flex items-center justify-center gap-3 py-3 rounded-2xl"
      style={{
        background: `linear-gradient(135deg, ${GOLD}18, ${GOLD_LO}0A)`,
        border: `1.5px dashed ${GOLD}88`,
        boxShadow: `0 0 18px ${GOLD}22, inset 0 0 12px ${GOLD}15`,
      }}
      data-testid="destiny-new">
      <div className="w-8 h-8 rounded-full flex items-center justify-center"
        style={{ background: `radial-gradient(circle, ${GOLD}44, transparent 70%)`, border: `1px solid ${GOLD}` }}>
        <Plus size={16} color={GOLD_HI}/>
      </div>
      <div className="text-left">
        <div className="font-['Outfit'] text-[11px] uppercase tracking-[0.28em]" style={{ color: GOLD_HI }}>Forge New Destiny</div>
        <div className="font-['Cormorant_Garamond'] italic text-xs" style={{ color: `${GOLD}88` }}>Ignite a new campaign</div>
      </div>
    </button>

    <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
      {destinies.map((d, idx) => {
        const total = d.steps?.length || 0;
        const done = (d.steps || []).filter((s) => s.status === 'completed').length;
        const pct = total > 0 ? Math.round((done / total) * 100) : 0;
        const allDone = total > 0 && done === total;
        return (
          <button key={d.id} type="button" onClick={() => onOpenDestiny(d.id)}
            className="relative text-left p-4 rounded-2xl overflow-hidden"
            style={{
              background: `linear-gradient(160deg, ${NAVY_HI}, ${NAVY})`,
              border: `1px solid ${GOLD}55`,
              boxShadow: `0 0 18px ${GOLD}22, inset 0 0 10px rgba(255,255,255,0.04)`,
            }}
            data-testid={`destiny-card-${idx}`}>
            <div className="flex items-start gap-3">
              {allDone ? <NexusCrownIcon/> : <NexusCardGem idx={idx}/>}
              <div className="flex-1 min-w-0">
                <p className="font-['Outfit'] text-[9px] uppercase tracking-[0.22em]" style={{ color: `${GOLD}AA` }}>
                  {allDone ? 'Conquered' : 'Campaign'}
                </p>
                <h3 className="font-['Cormorant_Garamond'] text-lg leading-tight truncate" style={{ color: GOLD_HI }}>{d.title}</h3>
              </div>
            </div>
            <div className="mt-3">
              <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
                <div className="h-full rounded-full" style={{
                  width: `${pct}%`,
                  background: `linear-gradient(90deg, ${GOLD_LO}, ${GOLD}, ${GOLD_HI})`,
                  boxShadow: `0 0 8px ${GOLD}AA`,
                }}/>
              </div>
              <div className="flex items-center justify-between mt-1.5 font-['Outfit'] text-[10px]">
                <span style={{ color: `${GOLD}99` }}>{done} / {total} gems</span>
                <span style={{ color: GOLD_HI }}>{pct}%</span>
              </div>
            </div>
          </button>
        );
      })}
    </div>
  </div>
);

/* ───────────────────────── Root ──────────────────────────────────────── */
export const DestinyCodex = ({ onClose }) => {
  const [state, setState]       = useState(() => loadState());
  const [view, setView]         = useState('nexus');
  const [activeId, setActiveId] = useState(null);
  const [subTab, setSubTab]     = useState('map'); // 'map' | 'schedule' | 'step' — active tab inside a destiny
  const [flash, setFlash]       = useState({ key: 0, color: GOLD_HI });
  const [fuseSpark, setFuseSpark] = useState({ key: 0, color: GOLD_HI });
  const { user } = useAuth();
  const userPicture = user?.profile_picture || user?.picture || null;
  // Resilient name pipeline: trim, strip @domain from emails, blank if literally empty.
  const rawName = (user?.nickname || user?.name || user?.display_name || user?.email || '').trim();
  const userName = rawName ? rawName.split('@')[0].trim() : '';

  useEffect(() => { saveState(state); }, [state]);

  const activeDestiny = state.destinies.find((d) => d.id === activeId) || null;

  const handleCreate = useCallback((destiny) => {
    setState((prev) => ({ destinies: [...prev.destinies, destiny] }));
    setActiveId(destiny.id);
    setSubTab('map');
    setView('destiny');
  }, []);

  const handleConquer = useCallback((stepIdx, gemColor) => {
    setState((prev) => {
      const dests = prev.destinies.map((d) => {
        if (d.id !== activeId) return d;
        if (!d.steps[stepIdx] || d.steps[stepIdx].status === 'completed') return d;
        const steps = d.steps.map((s, i) => i === stepIdx ? { ...s, status: 'completed' } : s);
        const next = steps.findIndex((s) => s.status !== 'completed');
        return { ...d, steps, currentStepIndex: next === -1 ? steps.length : next };
      });
      return { destinies: dests };
    });
    setFlash((f) => ({ key: f.key + 1, color: gemColor || GOLD_HI }));
    setFuseSpark((f) => ({ key: f.key + 1, color: gemColor || GOLD_HI }));
    pingAuraFuse('destiny');
  }, [activeId]);

  const handleDelete = useCallback(() => {
    if (!activeId) return;
    if (!window.confirm('Abandon this destiny? The path will be erased.')) return;
    setState((prev) => ({ destinies: prev.destinies.filter((d) => d.id !== activeId) }));
    setActiveId(null);
    setView('nexus');
  }, [activeId]);

  const handleSaveSchedule = useCallback((schedule, days) => {
    setState((prev) => ({
      destinies: prev.destinies.map((d) =>
        d.id === activeId
          ? { ...d, schedule, scheduleDays: days, fusedSteps: d.fusedSteps || {} }
          : d
      ),
    }));
  }, [activeId]);

  const handleFuseDay = useCallback((day, text) => {
    setState((prev) => ({
      destinies: prev.destinies.map((d) => {
        if (d.id !== activeId) return d;
        const fusedSteps = { ...(d.fusedSteps || {}) };
        fusedSteps[day] = { text, fused_at: new Date().toISOString() };
        return { ...d, fusedSteps };
      }),
    }));
    setFuseSpark((f) => ({ key: f.key + 1, color: GOLD_HI }));
    pingAuraFuse('destiny');
  }, [activeId]);

  const handleResetCycle = useCallback(() => {
    if (!window.confirm('Reset this cycle? All fused days will be cleared. The schedule itself stays.')) return;
    setState((prev) => ({
      destinies: prev.destinies.map((d) =>
        d.id === activeId ? { ...d, fusedSteps: {} } : d
      ),
    }));
  }, [activeId]);

  const openDestiny = (id) => {
    setActiveId(id);
    setSubTab('map');
    setView('destiny');
  };

  const inDestiny = view === 'destiny';
  const bodyScroll = inDestiny ? (subTab === 'map' ? 'overflow-hidden' : 'overflow-y-auto') : 'overflow-y-auto';

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[110] overflow-hidden flex flex-col imperium-shell"
      style={{ background: '#020203' }}
      data-testid="destiny-codex-modal">
      {/* Compact header (always visible) */}
      <div className="relative z-20 flex-shrink-0 backdrop-blur-md"
        style={{ background: `linear-gradient(180deg, ${NAVY}E6, ${NAVY}99)`, borderBottom: `1px solid ${GOLD}33` }}>
        <div className="flex items-center justify-between px-4 py-2.5">
          <div className="flex items-center gap-2 min-w-0">
            {!inDestiny && <BackPill onClick={onClose} accent="#7C9BFF" testId="destiny-close"/>}
            <DestinyTileIcon size={22}/>
            <span className="font-['Outfit'] text-[10px] uppercase tracking-[0.32em] truncate" style={{ color: GOLD }}>
              The Destiny Codex
            </span>
          </div>
          <div className="flex items-center gap-2">
            {inDestiny ? (
              <>
                <button type="button"
                  onClick={() => { setActiveId(null); setView('nexus'); }}
                  className="px-3 py-1.5 rounded-full font-['JetBrains_Mono',monospace] text-[10px] uppercase tracking-[0.2em]"
                  data-testid="destiny-back-nexus"
                  style={{ border: `1px solid ${GOLD}55`, color: GOLD }}>
                  <ChevronLeft size={12} className="inline -mt-0.5 mr-1"/> Nexus
                </button>
                <button type="button"
                  onClick={handleDelete}
                  className="w-9 h-9 rounded-full flex items-center justify-center"
                  aria-label="Abandon destiny" data-testid="destiny-delete"
                  style={{ border: `1px solid ${GOLD}55`, color: `${GOLD}AA` }}>
                  <Trash2 size={15}/>
                </button>
              </>
            ) : null}
          </div>
        </div>
      </div>

      {/* Master Tab bar — visible only inside a destiny */}
      {inDestiny && activeDestiny && (
        <MasterTabBar subTab={subTab} onChange={setSubTab}/>
      )}

      {/* Body */}
      <div className={`relative flex-1 ${bodyScroll}`}>
        {view === 'nexus' && (
          <NexusOfFates
            destinies={state.destinies}
            onOpenDestiny={openDestiny}
            onNewDestiny={() => setView('create')}
          />
        )}
        {view === 'create' && (
          <ManifestationLedger onBack={() => setView('nexus')} onCreate={handleCreate}/>
        )}
        {inDestiny && activeDestiny && subTab === 'map' && (
          <PathOfConquest
            destiny={activeDestiny}
            onConquer={handleConquer}
            onDelete={handleDelete}
            flashKey={flash.key}
            flashColor={flash.color}
            userPicture={userPicture}
            userName={userName}
            fuseSparkKey={fuseSpark.key}
            fuseSparkColor={fuseSpark.color}
          />
        )}
        {inDestiny && activeDestiny && subTab === 'schedule' && (
          <DestinyScheduleTab
            destiny={activeDestiny}
            onSaveSchedule={handleSaveSchedule}
            onResetCycle={handleResetCycle}
          />
        )}
        {inDestiny && activeDestiny && subTab === 'step' && (
          <DestinyStepTab
            destiny={activeDestiny}
            onFuseDay={handleFuseDay}
            onResetCycle={handleResetCycle}
          />
        )}
      </div>
    </motion.div>
  );
};

export default DestinyCodex;
